import { Box, Card, CardContent, Grid, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import SecurityIcon from '@mui/icons-material/Security'
import RateReviewIcon from '@mui/icons-material/RateReview'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import { motion } from 'framer-motion'

export const WhyPeopleLikeUs = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const features = [
    {
      icon: LocalShippingIcon,
      title: 'Home Delivery',
      description: 'Fast and reliable delivery right to your doorstep within 2-3 business days'
    },
    {
      icon: SecurityIcon,
      title: 'Trusted Service',
      description: 'Your trust is our priority with secure payments and authentic products'
    },
    {
      icon: RateReviewIcon,
      title: 'Best Reviews',
      description: 'Join thousands of satisfied customers who love our quality and service'
    },
    {
      icon: SupportAgentIcon,
      title: 'Expert Support',
      description: '24/7 customer support team ready to help with any questions or concerns'
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  }

  return (
    <Box sx={{ py: { xs: 5, md: 7 }, px: isMobile ? 2 : 4, backgroundColor: '#fff' }}>
      <Stack spacing={4} maxWidth="1180px" mx="auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Stack spacing={1} alignItems="center" textAlign="center">
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 900,
                color: 'common.black',
                fontSize: isMobile ? '1.9rem' : '2.6rem'
              }}
            >
              Why People Love Us
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '1.1rem',
                maxWidth: 680
              }}
            >
              We're committed to excellence in every aspect of our service
            </Typography>
          </Stack>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
        >
          <Grid container spacing={3}>
            {features.map((feature, idx) => {
              const Icon = feature.icon
              return (
                <Grid item xs={12} sm={6} md={3} key={idx}>
                  <motion.div variants={itemVariants}>
                    <Card
                      sx={{
                        height: '100%',
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        boxShadow: 'none',
                        '&:hover': {
                          boxShadow: '0 14px 30px rgba(0, 0, 0, 0.10)',
                          transform: 'translateY(-8px)',
                          borderColor: 'primary.main'
                        }
                      }}
                    >
                      <CardContent>
                        <Stack spacing={2} alignItems="center">
                          <Box
                            sx={{
                              p: 2,
                              backgroundColor: '#111',
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Icon 
                              sx={{ 
                                fontSize: '2.5rem', 
                                color: '#fff'
                              }} 
                            />
                          </Box>
                          <Typography 
                            variant="h6" 
                            sx={{ fontWeight: 600 }}
                          >
                            {feature.title}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'text.secondary',
                              lineHeight: 1.6
                            }}
                          >
                            {feature.description}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              )
            })}
          </Grid>
        </motion.div>
      </Stack>
    </Box>
  )
}
